import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Button, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import api from "common/api";
import { ISubject, ITeacher } from "common/types";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import hr from "date-fns/locale/hr";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import usePageTitle from "common/usePageTitle";

interface ISelectOption {
  value: string;
  label: string;
}

function groupTeachersBySubject(teachersList: ITeacher[]) {
  const subjectsGroup: any = {};

  teachersList.forEach((teacher) => {
    if (teacher.profile?.subjects?.length > 0) {
      teacher.profile.subjects.forEach((subject: any) => {
        if (!subjectsGroup[subject.name]) {
          subjectsGroup[subject.name] = [teacher];
        } else {
          subjectsGroup[subject.name].push(teacher);
        }
      });
    } else {
      if (!subjectsGroup["Nema predmeta"]) {
        subjectsGroup["Nema predmeta"] = [teacher];
      }
    }
  });

  return subjectsGroup;
}

const findTeacherOption = (teacherId: string, options: any[]) => {
  for (let i = 0; i < options.length; i++) {
    const foundOption = options[i].options.find(
      (option: any) => option.value === teacherId,
    );
    if (foundOption) {
      return foundOption;
    }
  }
  return null;
};

function formatGroupedTeachersOptions(groupedTeachers: any) {
  return Object.keys(groupedTeachers).map((subject) => ({
    label: subject,
    options: groupedTeachers[subject].map((teacher: any) => ({
      label: `${teacher.first_name} ${teacher.last_name}`,
      value: teacher.user_id,
    })),
  }));
}

function CourseAddEditPage() {
  const [course, setCourse] = useState<any>(null);
  const [subjects, setSubjects] = useState<ISubject[]>([]);
  const [groupedTeachersOptions, setGroupedTeachersOptions] = useState<any>({});
  const [initialValues, setInitialValues] = useState({
    title: "",
    description: "",
    subject: "",
    meet_url: "",
    times: "",
    start_date: new Date(),
    end_date: new Date(new Date().getTime() + 60 * 90 * 1000),
    appointment_count: "",
    teachers: [""],
  });

  const navigation = useNavigate();
  const { courseId } = useParams();

  usePageTitle(courseId ? "Izmjena grupe" : "Dodavanje grupe");

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Naziv je obavezan"),
    description: Yup.string().required("Opis je obavezan"),
    subject: Yup.string().required("Potrebno je odabrati predmet"),
    times: Yup.string().required("Termin je obavezan"),
    teachers: Yup.array()
      .of(
        Yup.string()
          .required("Potrebno je odabrati nastavnika")
          .min(1, "Potrebno je odabrati nastavnika"),
      )
      .required("Potrebno je odabrati nastavnika"),
    ...(courseId
      ? {}
      : {
          meet_url: Yup.string().required("Meeting URL je obavezan"),
          start_date: Yup.date().required("Datum početka je obavezan"),
          end_date: Yup.date()
            .required("Datum kraja je obavezan")
            .when("start_date", (start_date: any, yup: any) =>
              start_date
                ? yup.min(
                    start_date,
                    "Datum kraja mora biti nakon datuma početka",
                  )
                : yup,
            ),
          appointment_count: Yup.number()
            .required("Broj termina je obavezan")
            .positive("Broj termina mora biti pozitivan")
            .integer("Broj termina mora biti cijeli broj"),
        }),
  });

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await api.get(`/courses/${courseId}/`);
        setCourse(response.data);
        setInitialValues({
          ...initialValues,
          title: response.data.title,
          description: response.data.description,
          subject: response.data.subjects[0].id,
          times: response.data.times,
          teachers: [response.data.teachers[0].user_id],
        });
      } catch (error) {
        console.error(error);
      }
    };

    const fetchSubjects = async () => {
      try {
        const response = await api.get("/subjects/");
        setSubjects(response.data);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchTeachers = async () => {
      try {
        const response = await api.get("/teachers/");
        setGroupedTeachersOptions(
          formatGroupedTeachersOptions(groupTeachersBySubject(response.data)),
        );
      } catch (error) {
        console.error("error");
      }
    };

    if (courseId) {
      fetchCourse();
    }
    fetchSubjects();
    fetchTeachers();
  }, [courseId]);

  const handleSubmit = async (values: any) => {
    const appointments = [];
    for (let i = 0; i < values.appointment_count; i++) {
      const startDate = new Date(values.start_date);
      startDate.setDate(startDate.getDate() + i * 7);

      const endDate = new Date(values.end_date);
      endDate.setDate(endDate.getDate() + i * 7);

      appointments.push({
        start: startDate.toISOString(),
        end: endDate.toISOString(),
        meet_url: values.meet_url,
        text: "",
      });
    }

    const requestData: any = {
      title: values.title,
      description: values.description,
      subjects: [values.subject],
      times: values.times,
      teachers: values.teachers,
    };

    if (!course) {
      requestData.students = [];
      requestData.appointments = appointments;
    }

    try {
      if (course) {
        await api.patch(`/courses/${course.id}/`, requestData);
        toast.success("Grupa uspješno ažurirana");
        navigation("/grupe-admin");
        return;
      } else {
        await api.post("/courses/", requestData);
        toast.success("Grupa uspješno dodana");
      }
    } catch (error) {
      console.error(error);
      toast.error("Greška prilikom spremanja grupe");
    }
  };

  if (courseId && (!course || !subjects || !groupedTeachersOptions)) {
    return <Spinner animation="border" />;
  }

  return (
    <Container>
      <h2 className="mb-4">{course ? "Izmjena grupe" : "Dodavanje grupe"}</h2>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          handleSubmit(values);
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
          errors,
          touched,
        }) => (
          <Form className="mt-4" onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col>
                <Form.Label>Odabir nastavnika</Form.Label>
                <Select
                  options={groupedTeachersOptions}
                  onChange={(selectedOption: ISelectOption | null) => {
                    setFieldValue("teachers", [selectedOption?.value]);
                  }}
                  value={findTeacherOption(
                    values.teachers[0],
                    groupedTeachersOptions,
                  )}
                  className={`${
                    touched.teachers && !!errors.teachers && "is-invalid"
                  }`}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.teachers}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>Naziv Grupe</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite naziv grupe"
                  name="title"
                  onChange={handleChange}
                  value={values.title}
                  isInvalid={touched.title && !!errors.title}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.teachers && typeof errors.teachers === "string"
                    ? errors.teachers
                    : "Pogrešan unos"}
                </Form.Control.Feedback>
              </Col>

              <Col>
                <Form.Label>Termin:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Unesite termine grupe (npr. petak 18:00-19:00)"
                  name="times"
                  onChange={handleChange}
                  value={values.times}
                  isInvalid={touched.times && !!errors.times}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.times}
                </Form.Control.Feedback>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Label>Opis</Form.Label>
                <Form.Control
                  as="textarea"
                  placeholder="Unesite opis grupe"
                  name="description"
                  onChange={handleChange}
                  value={values.description}
                  isInvalid={touched.description && !!errors.description}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.description}
                </Form.Control.Feedback>
              </Col>
            </Row>
            {!courseId && (
              <Row className="mb-3">
                <Col>
                  <Form.Label>Google Meet URL</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Unesite URL adresu za Google Meet"
                    name="meet_url"
                    onChange={handleChange}
                    value={values.meet_url}
                    isInvalid={touched.meet_url && !!errors.meet_url}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.meet_url}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col>
                <Form.Label>Predmet</Form.Label>
                <Form.Control
                  as="select"
                  name="subject"
                  onChange={handleChange}
                  value={values.subject}
                  isInvalid={touched.subject && !!errors.subject}
                >
                  <option value="">Odaberite predmet</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  {errors.subject}
                </Form.Control.Feedback>
              </Col>
            </Row>

            {!courseId && (
              <Row className="mb-3">
                <Col>
                  <Form.Label>Datum i vrijeme početka termina</Form.Label>{" "}
                  <DatePicker
                    selected={values.start_date}
                    onChange={(date: Date | null) =>
                      setFieldValue("start_date", date)
                    }
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="Vrijeme"
                    dateFormat="dd.MM.yyyy HH:mm"
                    locale={hr}
                    placeholderText="Odaberi početak prvog termina"
                    className={`form-control ${
                      touched.start_date && !!errors.start_date && "is-invalid"
                    }`}
                    wrapperClassName="w-100"
                    showIcon
                    customInput={<Form.Control />}
                  />
                  <Form.Control.Feedback type="invalid">
                    <>{errors.start_date}</>
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Label>Datum i vrijeme kraja prvog termina</Form.Label>{" "}
                  <DatePicker
                    selected={values.end_date}
                    onChange={(date: Date | null) =>
                      setFieldValue("end_date", date)
                    }
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    timeCaption="Vrijeme"
                    dateFormat="dd.MM.yyyy HH:mm"
                    locale={hr}
                    placeholderText="Odaberi kraj prvog termina"
                    className={`form-control ${
                      touched.end_date && !!errors.end_date && "is-invalid"
                    }`}
                    wrapperClassName="w-100"
                    showIcon
                    customInput={<Form.Control />}
                  />
                  <Form.Control.Feedback type="invalid">
                    <>{errors.end_date}</>
                  </Form.Control.Feedback>
                </Col>
                <Col>
                  <Form.Label>Broj termina</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Unesite broj termina"
                    name="appointment_count"
                    onChange={handleChange}
                    value={values.appointment_count}
                    isInvalid={
                      touched.appointment_count && !!errors.appointment_count
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.appointment_count}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            )}
            <Button variant="primary" type="submit">
              Spremi grupu
            </Button>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

export default CourseAddEditPage;
